import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import { api, type Tournament, type TournamentDivision } from "../lib/api";
import Icon from "./icons";

interface ActionCard {
  label: string;
  shortLabel: string;
  description: string;
  icon: string;
  to: string;
  activePrefixes: string[];
}

interface AdminTournamentOperationPanelProps {
  tournamentId: number;
}

// Eduardo 2026-09-21: mismos iconos de línea que la vista jugador (fuera los
// emojis) y "Reportes" solo cuando el torneo deja que los jugadores reporten
// sus resultados; apagado, el botón solo confunde.
function buildActions(
  tournamentId: number,
  usesCourtCalendar: boolean,
  playersReportResults: boolean,
  refereesEnabled = true,
): ActionCard[] {
  const base = `/admin/tournaments/${tournamentId}`;
  const actions: ActionCard[] = [
    {
      label: "Draw",
      shortLabel: "Draw",
      description: "Jornadas, retos, draw y enfrentamientos.",
      icon: "bracket",
      to: `${base}/rounds`,
      activePrefixes: [`${base}/rounds`],
    },
    {
      label: "Resultados",
      shortLabel: "Resultados",
      description: "Consulta y publica estatus, scores y acciones por partido.",
      icon: "check",
      to: `${base}/results`,
      activePrefixes: [`${base}/results`],
    },
    ...(playersReportResults
      ? [
          {
            label: "Resultados reportados",
            shortLabel: "Reportes",
            description: "Revisión de reportes enviados por jugadores.",
            icon: "clipboard",
            to: `${base}/result-reports`,
            activePrefixes: [`${base}/result-reports`],
          },
        ]
      : []),
    // Siempre a la vista (Eduardo 2026-10-03): «¿Este torneo usa calendario
    // de canchas?» se decide solo en Calendario; escondido con «No», ya no
    // habría dónde regresarlo a «Sí».
    {
      label: "Calendario",
      shortLabel: "Calendario",
      description: usesCourtCalendar
        ? "Calendario de partidos, canchas y horarios."
        : "Sin calendario de canchas: aquí lo cambias y ves los partidos.",
      icon: "calendar",
      to: `${base}/schedule`,
      activePrefixes: [`${base}/schedule`, `${base}/calendar`],
    },
    // Árbitros (Eduardo 2026-10-16): invitar, asignar y avisar. Solo con
    // «Sí» en Comunicación (paso 4, Eduardo 2026-10-17).
    ...(refereesEnabled
      ? [
          {
            label: "Árbitros",
            shortLabel: "Árbitros",
            description: "Invita árbitros, asígnalos a partidos y avísales su lista del día.",
            icon: "clipboard-check",
            to: `${base}/referees`,
            activePrefixes: [`${base}/referees`],
          },
        ]
      : []),
    {
      label: "Grupos",
      shortLabel: "Grupos",
      description: "Organización por grupos y asignaciones.",
      icon: "grid",
      to: `${base}/groups`,
      activePrefixes: [`${base}/groups`],
    },
    {
      label: "Ranking",
      shortLabel: "Ranking",
      description: "Tabla y posiciones actualizadas del torneo.",
      icon: "chart",
      to: `${base}/standings`,
      activePrefixes: [`${base}/standings`],
    },
    {
      label: "Jugadores",
      shortLabel: "Jugadores",
      description: "Alta, baja, grupos y rank de participantes.",
      icon: "users",
      to: `${base}/players`,
      activePrefixes: [`${base}/players`],
    },
    {
      label: "Reglamento",
      shortLabel: "Reglamento",
      description: "Reglas del torneo y aceptación de jugadores.",
      icon: "book",
      to: `${base}/rules`,
      activePrefixes: [`${base}/rules`],
    },
    {
      label: "Comunicados",
      shortLabel: "Avisos",
      description: "Avisos y mensajes del torneo.",
      icon: "megaphone",
      to: `${base}/communications`,
      activePrefixes: [`${base}/communications`, `${base}/announcements`],
    },
    {
      label: "Configuración",
      shortLabel: "Configuración",
      description: "Nombre, sede, fechas, modalidad y configuración general.",
      icon: "settings",
      to: `${base}/edit`,
      activePrefixes: [`${base}/edit`, `${base}/setup/settings`, `${base}/settings`],
    },
  ];
  return actions;
}

function isActionActive(pathname: string, action: ActionCard): boolean {
  const normalizedPath = pathname.replace(/\/$/, "");
  return action.activePrefixes.some((prefix) => {
    const normalizedPrefix = prefix.replace(/\/$/, "");
    return normalizedPath === normalizedPrefix || normalizedPath.startsWith(`${normalizedPrefix}/`);
  });
}

function appendDivisionQuery(to: string, divisionId: string | null): string {
  if (!divisionId) return to;
  return `${to}?division=${encodeURIComponent(divisionId)}`;
}

export default function AdminTournamentOperationPanel({
  tournamentId,
}: AdminTournamentOperationPanelProps) {
  const { token } = useAuth();
  const location = useLocation();
  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [divisions, setDivisions] = useState<TournamentDivision[]>([]);
  const [usesCourtCalendar, setUsesCourtCalendar] = useState(true);
  const [playersReportResults, setPlayersReportResults] = useState(false);
  const [refereesEnabled, setRefereesEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [mobileMoreOpen, setMobileMoreOpen] = useState(false);

  const divisionId = useMemo(
    () => new URLSearchParams(location.search).get("division"),
    [location.search],
  );

  useEffect(() => {
    if (!token || Number.isNaN(tournamentId)) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const [tournamentResponse, divisionsResponse] = await Promise.all([
          api.tournaments.get(token, tournamentId),
          api.tournaments.listDivisions(token, tournamentId).catch(() => ({
            data: { divisions: [] as TournamentDivision[] },
          })),
        ]);
        if (cancelled) return;
        setTournament(tournamentResponse.data.tournament);
        setUsesCourtCalendar(Boolean(tournamentResponse.data.settings.uses_court_calendar));
        setPlayersReportResults(Boolean(tournamentResponse.data.settings.allow_player_result_reporting));
        setRefereesEnabled(Boolean(tournamentResponse.data.settings.referees_enabled));
        setDivisions(divisionsResponse.data.divisions);
      } catch {
        if (cancelled) return;
        setTournament(null);
        setDivisions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, tournamentId]);

  if (Number.isNaN(tournamentId)) return null;

  // Quien ya está en Reportes (liga directa) conserva el botón aunque esté apagado.
  const onResultReports = location.pathname.includes("/result-reports");
  const actions = buildActions(
    tournament?.id ?? tournamentId,
    usesCourtCalendar,
    playersReportResults || onResultReports,
    refereesEnabled,
  );
  const selectedDivision = divisionId
    ? divisions.find((division) => String(division.id) === divisionId)
    : null;
  const contextLabel = tournament?.name ?? `Torneo #${tournamentId}`;
  const divisionLabel = selectedDivision?.name ?? (divisionId ? `Categoría ${divisionId}` : null);
  const activeAction = actions.find((action) => isActionActive(location.pathname, action)) ?? null;
  const activeActionIndex = activeAction ? actions.findIndex((action) => action.to === activeAction.to) : -1;
  const mobilePrimaryActions = activeAction && activeActionIndex >= 4
    ? [...actions.slice(0, 3), activeAction]
    : actions.slice(0, 4);
  const mobilePrimaryKeys = new Set(mobilePrimaryActions.map((action) => action.to));
  const mobileMoreActions = actions.filter((action) => !mobilePrimaryKeys.has(action.to));

  function renderActionLink(action: ActionCard, className = "tournament-operation-dock__link") {
    const active = isActionActive(location.pathname, action);
    return (
      <Link
        key={action.to}
        aria-current={active ? "page" : undefined}
        className={`${className}${active ? " is-active" : ""}`}
        to={appendDivisionQuery(action.to, divisionId)}
        title={`${action.label}: ${action.description}`}
        onClick={() => setMobileMoreOpen(false)}
      >
        <span className="tournament-operation-dock__icon" aria-hidden="true">
          <Icon name={action.icon} />
        </span>
        <small>{action.shortLabel}</small>
      </Link>
    );
  }

  return (
    <nav className="tournament-operation-dock" aria-label="Operación del torneo">
      <div className="tournament-operation-dock__context" aria-live="polite">
        <span className="tournament-operation-dock__eyebrow">Operación del torneo</span>
        <strong title={contextLabel}>{contextLabel}</strong>
        {divisionLabel && <small title={divisionLabel}>{divisionLabel}</small>}
      </div>
      {loading ? (
        <p className="tournament-operation-dock__loading">Cargando secciones…</p>
      ) : (
        <>
          <div className="tournament-operation-dock__actions tournament-operation-dock__actions--desktop">
            {actions.map((action) => renderActionLink(action))}
          </div>
          <div className="tournament-operation-dock__mobile-shell">
            <div className="tournament-operation-dock__mobile-primary">
              {mobilePrimaryActions.map((action) => renderActionLink(action))}
              {mobileMoreActions.length > 0 && (
                <button
                  type="button"
                  className={`tournament-operation-dock__link tournament-operation-dock__more-button${mobileMoreOpen ? " is-active" : ""}`}
                  aria-expanded={mobileMoreOpen}
                  aria-controls="tournament-operation-dock-more"
                  onClick={() => setMobileMoreOpen((open) => !open)}
                >
                  <span className="tournament-operation-dock__icon" aria-hidden="true"><Icon name="more" /></span>
                  <small>Más</small>
                </button>
              )}
            </div>
            {mobileMoreOpen && mobileMoreActions.length > 0 && (
              <div className="tournament-operation-dock__more-panel" id="tournament-operation-dock-more">
                {mobileMoreActions.map((action) => renderActionLink(action, "tournament-operation-dock__more-link"))}
              </div>
            )}
          </div>
        </>
      )}
    </nav>
  );
}
